// Catalogue statique des templates d'architecture
// Nœuds au format ReactFlow attendu par le canvas :
//   type: 'custom'  → CustomNode (avec icône)
//   data.type: clé Terraform exacte (prefix détermine le cloud)
//   data.icon: chemin SVG résolu au build (nécessaire car enrichAllNodes n'est pas appelé au chargement DB)
//   data.label: nom d'affichage

import { azureIconsByTypeTop100 } from '../../Config/azure/azure-iconsByType.top100';
import { awsIconsByTypeTop100 }   from '../../Config/aws/aws-iconsByType.top100';
import { gcpIconsByTypeTop100 }   from '../../Config/gcp/gcp-iconsByType.top100';
import { fabricIconsByTypeTop100 } from '../../Config/fabric/fabric-iconsByType.top100';

function resolveIcon(tfType: string): string {
  if (tfType.startsWith('aws_'))     return (awsIconsByTypeTop100 as any)[tfType]    || '/icons/aws/Arch_General_48.svg';
  if (tfType.startsWith('google_'))  return (gcpIconsByTypeTop100 as any)[tfType]    || '/icons/gcp/google-cloud.svg';
  if (tfType.startsWith('fabric_'))  return (fabricIconsByTypeTop100 as any)[tfType] || '/icons/fabric/fabric_48_color.svg';
  // azurerm_ + fallback Azure
  return (azureIconsByTypeTop100 as any)[tfType] || '/icons/azure/azure.svg';
}

export type TemplateProvider = 'azure' | 'aws' | 'gcp' | 'fabric' | 'multicloud';
export type TemplateCategory =
  | 'web-app' | 'microservices' | 'data' | 'serverless'
  | 'network' | 'security' | 'analytics' | 'ml' | 'fabric-analytics' | 'iot';
export type TemplateDifficulty = 'beginner' | 'intermediate' | 'advanced';

export interface TemplateData {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  category: TemplateCategory;
  provider: TemplateProvider;
  difficulty: TemplateDifficulty;
  resourcesCount: number;
  deploymentTime: string;
  tags: string[];
  icon: string;
  popular?: boolean;
  features: string[];
  diagramData: { nodes: any[]; edges: any[] };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const n = (id: string, tfType: string, label: string, x: number, y: number) => ({
  id,
  type: 'custom',
  position: { x, y },
  data: {
    type: tfType,
    label,
    icon: resolveIcon(tfType),  // Pré-résolu : stocké en DB, affiché sans enrichAllNodes
  },
});

// Couleurs cloud (mirror de canvasEdgeColor dans FlowEditor)
const EDGE_COLORS: Record<TemplateProvider, string> = {
  azure:      '#3B82F6',
  aws:        '#F97316',
  gcp:        '#22C55E',
  fabric:     '#038387',
  multicloud: '#3B82F6',
};

// Edge factory simple — la couleur est appliquée par colorizeTemplate() à l'export
const e = (id: string, source: string, target: string, label?: string) => ({
  id, source, target,
  type: 'smoothstep',
  ...(label ? {
    label,
    labelStyle: { fontSize: 10, fill: '#6B7280' },
    labelBgStyle: { fill: 'white', fillOpacity: 0.85 },
  } : {}),
});

// Post-traitement : injecte le style couleur sur tous les edges du template
function colorizeTemplate(tpl: TemplateData): TemplateData {
  const color = EDGE_COLORS[tpl.provider];
  return {
    ...tpl,
    diagramData: {
      ...tpl.diagramData,
      edges: tpl.diagramData.edges.map(edge => ({
        ...edge,
        style: { stroke: color, strokeWidth: 2 },
        markerEnd: { type: 'arrowclosed', color },
      })),
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// AZURE TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

const azureWebApp3Tier: TemplateData = {
  id: 'azure-web-app-3tier',
  name: 'Application Web 3-Tiers',
  description: 'Architecture classique frontend / backend API / base de données avec load balancer et cache Redis.',
  longDescription: 'Architecture 3-tiers complète sur Azure. Le trafic entre par un Application Gateway (WAF), est distribué sur plusieurs instances App Service, et persiste dans Azure SQL avec un cache Redis pour les sessions et données fréquentes. Key Vault centralise les secrets et Application Insights assure l\'observabilité.',
  category: 'web-app', provider: 'azure', difficulty: 'beginner',
  resourcesCount: 8, deploymentTime: '15 min',
  tags: ['Web App', 'SQL', 'Redis', 'App Gateway'],
  icon: '🌐', popular: true,
  features: [
    'Application Gateway avec WAF',
    'Azure App Service (Container App)',
    'Azure SQL Database (HA)',
    'Azure Cache for Redis',
    'Azure Storage (assets statiques)',
    'Key Vault pour les secrets',
    'Application Insights',
  ],
  diagramData: {
    nodes: [
      n('agw',   'azurerm_application_gateway',  'Application Gateway',  100, 200),
      n('app1',  'azurerm_container_app',         'App Service',          380, 100),
      n('app2',  'azurerm_container_app',         'App Service',          380, 300),
      n('redis', 'azurerm_redis_cache',            'Azure Cache Redis',   650, 100),
      n('sql',   'azurerm_mssql_database',         'Azure SQL Database',  650, 300),
      n('stor',  'azurerm_storage_account',        'Azure Storage',       900, 100),
      n('kv',    'azurerm_key_vault',              'Key Vault',           900, 300),
      n('ai',    'azurerm_application_insights',   'Application Insights', 650, 480),
    ],
    edges: [
      e('e1', 'agw',  'app1', 'HTTP/S'),
      e('e2', 'agw',  'app2', 'HTTP/S'),
      e('e3', 'app1', 'redis', 'Cache'),
      e('e4', 'app2', 'redis', 'Cache'),
      e('e5', 'app1', 'sql',   'SQL'),
      e('e6', 'app2', 'sql',   'SQL'),
      e('e7', 'app1', 'stor',  'Blob'),
      e('e8', 'app1', 'kv',    'Secrets'),
      e('e9', 'app1', 'ai',    'Telemetry'),
    ],
  },
};

const azureMicroservicesAKS: TemplateData = {
  id: 'azure-microservices-aks',
  name: 'Microservices sur AKS',
  description: 'Architecture microservices Kubernetes avec Istio service mesh, API Gateway et monitoring complet.',
  longDescription: 'Déploiement de microservices sur Azure Kubernetes Service. Azure API Management expose les services externement, AKS exécute les conteneurs, Service Bus découple les services, et Azure Container Registry stocke les images. Key Vault gère les secrets via le CSI Driver.',
  category: 'microservices', provider: 'azure', difficulty: 'advanced',
  resourcesCount: 7, deploymentTime: '30 min',
  tags: ['AKS', 'Kubernetes', 'Microservices', 'Service Bus'],
  icon: '🔷', popular: true,
  features: [
    'Azure Kubernetes Service (AKS)',
    'Azure API Management',
    'Azure Container Registry (ACR)',
    'Azure Service Bus (messaging)',
    'Key Vault (CSI driver)',
    'Application Insights',
    'Azure SQL Database',
  ],
  diagramData: {
    nodes: [
      n('apim', 'azurerm_api_management',      'API Management',      100, 250),
      n('aks',  'azurerm_kubernetes_cluster',  'AKS Cluster',         380, 150),
      n('acr',  'azurerm_container_registry',  'Container Registry',  380, 400),
      n('sb',   'azurerm_servicebus_namespace','Service Bus',         650, 150),
      n('kv',   'azurerm_key_vault',           'Key Vault',           650, 400),
      n('ai',   'azurerm_application_insights','Application Insights', 900, 150),
      n('sql',  'azurerm_mssql_database',      'Azure SQL',           900, 400),
    ],
    edges: [
      e('e1', 'apim', 'aks',  'Routes'),
      e('e2', 'acr',  'aks',  'Images'),
      e('e3', 'aks',  'sb',   'Events'),
      e('e4', 'aks',  'kv',   'Secrets'),
      e('e5', 'aks',  'ai',   'Metrics'),
      e('e6', 'aks',  'sql',  'Data'),
    ],
  },
};

const azureServerless: TemplateData = {
  id: 'azure-serverless-api',
  name: 'API Serverless Azure',
  description: 'API REST serverless avec Azure Functions, API Management et Cosmos DB pour une scalabilité infinie.',
  longDescription: 'Architecture entièrement serverless : Azure Functions pour la logique métier déclenchée via API Management, Cosmos DB pour la persistance multi-région, Event Grid pour l\'orchestration des événements, et Logic Apps pour les workflows à long terme.',
  category: 'serverless', provider: 'azure', difficulty: 'intermediate',
  resourcesCount: 6, deploymentTime: '10 min',
  tags: ['Serverless', 'Functions', 'Cosmos DB', 'Event Grid'],
  icon: '⚡',
  features: [
    'Azure Functions (Consumption plan)',
    'Azure API Management',
    'Azure Cosmos DB (multi-région)',
    'Azure Event Grid',
    'Azure Logic Apps',
    'Azure Key Vault',
  ],
  diagramData: {
    nodes: [
      n('apim',  'azurerm_api_management',   'API Management', 100, 200),
      n('fn1',   'azurerm_function_app',     'Function App',   380, 100),
      n('fn2',   'azurerm_function_app',     'Function App',   380, 300),
      n('cosmos','azurerm_cosmosdb_account', 'Cosmos DB',      650, 200),
      n('eg',    'azurerm_eventgrid_topic',  'Event Grid',     650, 400),
      n('la',    'azurerm_logic_app_workflow','Logic Apps',    900, 300),
    ],
    edges: [
      e('e1', 'apim', 'fn1',   'HTTP'),
      e('e2', 'apim', 'fn2',   'HTTP'),
      e('e3', 'fn1',  'cosmos','Read/Write'),
      e('e4', 'fn2',  'cosmos','Read/Write'),
      e('e5', 'fn1',  'eg',    'Events'),
      e('e6', 'eg',   'la',    'Trigger'),
    ],
  },
};

const azureDataLake: TemplateData = {
  id: 'azure-data-lake',
  name: 'Data Lake & Analytics',
  description: 'Pipeline de données complet avec ADLS Gen2, Azure Data Factory, Synapse Analytics et streaming.',
  longDescription: 'Architecture data lakehouse moderne : ingestion via Event Hubs et Stream Analytics, stockage dans Azure Storage (ADLS Gen2), transformation avec Databricks, exposition via Synapse Analytics et Synapse SQL Pool.',
  category: 'data', provider: 'azure', difficulty: 'advanced',
  resourcesCount: 7, deploymentTime: '25 min',
  tags: ['Data Lake', 'Synapse', 'Databricks', 'Data Factory'],
  icon: '📊',
  features: [
    'Azure Data Lake Storage Gen2',
    'Azure Data Factory (pipelines)',
    'Azure Databricks (transformation)',
    'Azure Synapse Analytics',
    'Azure Event Hubs (streaming)',
    'Stream Analytics',
    'Synapse SQL Pool',
  ],
  diagramData: {
    nodes: [
      n('eh',    'azurerm_eventhub_namespace',  'Event Hubs',        100, 200),
      n('adf',   'azurerm_data_factory',        'Data Factory',      380, 100),
      n('adls',  'azurerm_storage_account',     'ADLS Gen2',         650, 200),
      n('adb',   'azurerm_databricks_workspace','Databricks',        900, 100),
      n('syn',   'azurerm_synapse_workspace',   'Synapse Analytics', 900, 300),
      n('stream','azurerm_stream_analytics_job','Stream Analytics',  380, 300),
      n('pool',  'azurerm_synapse_sql_pool',    'Synapse SQL Pool', 1150, 200),
    ],
    edges: [
      e('e1', 'eh',    'stream','Stream'),
      e('e2', 'stream','adls',  'Load'),
      e('e3', 'adf',   'adls',  'Ingest'),
      e('e4', 'adls',  'adb',   'Transform'),
      e('e5', 'adb',   'syn',   'Load'),
      e('e6', 'syn',   'pool',  'SQL'),
    ],
  },
};

const azureLandingZone: TemplateData = {
  id: 'azure-landing-zone',
  name: 'Landing Zone Entreprise',
  description: 'Landing zone complète avec gouvernance, sécurité et conformité basée sur le Cloud Adoption Framework.',
  longDescription: 'Implémentation du CAF Azure : Policies pour la gouvernance, Defender + Sentinel pour la sécurité, réseau Hub-Spoke avec Firewall Azure, et ExpressRoute pour la connectivité hybride.',
  category: 'security', provider: 'azure', difficulty: 'advanced',
  resourcesCount: 7, deploymentTime: '40 min',
  tags: ['CAF', 'Governance', 'Security', 'Hub-Spoke'],
  icon: '🏢', popular: true,
  features: [
    'Azure Policy & Blueprints',
    'Microsoft Sentinel (SIEM)',
    'Azure Firewall Premium',
    'Hub-Spoke Network',
    'ExpressRoute',
    'Log Analytics Workspace',
    'Key Vault (centralisé)',
  ],
  diagramData: {
    nodes: [
      n('pol',  'azurerm_policy_assignment',                          'Azure Policy',   100, 100),
      n('sent', 'azurerm_sentinel_log_analytics_workspace_onboarding','Sentinel',       100, 300),
      n('kv',   'azurerm_key_vault',                                  'Key Vault',      350, 200),
      n('hub',  'azurerm_virtual_network',                            'Hub VNet',       600, 100),
      n('fw',   'azurerm_firewall',                                   'Azure Firewall', 600, 300),
      n('spoke','azurerm_virtual_network',                            'Spoke VNet',     850, 200),
      n('er',   'azurerm_express_route_circuit',                      'ExpressRoute',  1100, 200),
    ],
    edges: [
      e('e1', 'pol',  'hub',   'Enforce'),
      e('e2', 'sent', 'hub',   'Monitor'),
      e('e3', 'kv',   'hub',   'Secrets'),
      e('e4', 'hub',  'fw',    'Route'),
      e('e5', 'hub',  'spoke', 'Peering'),
      e('e6', 'fw',   'spoke', 'Inspect'),
      e('e7', 'hub',  'er',    'Connect'),
    ],
  },
};

const azureHubSpoke: TemplateData = {
  id: 'azure-hub-spoke',
  name: 'Réseau Hub-Spoke',
  description: 'Topologie réseau Hub-Spoke avec Azure Firewall, VPN Gateway et DNS privé pour connectivité hybride.',
  longDescription: 'Architecture réseau Hub-Spoke : services partagés centralisés dans un hub (Firewall, DNS, VPN), connectés en peering à plusieurs spokes dédiés aux workloads. Permet une sécurité centralisée et une gouvernance réseau simplifiée.',
  category: 'network', provider: 'azure', difficulty: 'intermediate',
  resourcesCount: 6, deploymentTime: '20 min',
  tags: ['Network', 'Hub-Spoke', 'Firewall', 'VPN'],
  icon: '🔗',
  features: [
    'Hub VNet (services centralisés)',
    'Azure Firewall Premium',
    'Azure VPN Gateway',
    'Azure DNS Privé',
    'Spoke VNets (workloads)',
    'Network Security Groups',
  ],
  diagramData: {
    nodes: [
      n('vpn',   'azurerm_vpn_gateway',    'VPN Gateway',  100, 200),
      n('hub',   'azurerm_virtual_network','Hub VNet',     350, 200),
      n('fw',    'azurerm_firewall',       'Firewall',     600, 100),
      n('dns',   'azurerm_dns_zone',       'Private DNS',  600, 300),
      n('spoke1','azurerm_virtual_network','Spoke VNet 1', 850, 100),
      n('spoke2','azurerm_virtual_network','Spoke VNet 2', 850, 300),
    ],
    edges: [
      e('e1', 'vpn',   'hub',    'On-prem'),
      e('e2', 'hub',   'fw',     'Route'),
      e('e3', 'hub',   'dns',    'Resolve'),
      e('e4', 'hub',   'spoke1', 'Peering'),
      e('e5', 'hub',   'spoke2', 'Peering'),
      e('e6', 'fw',    'spoke1', 'Inspect'),
      e('e7', 'fw',    'spoke2', 'Inspect'),
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// AWS TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

const awsWebApp: TemplateData = {
  id: 'aws-webapp-static',
  name: 'Site Web Statique AWS',
  description: 'Site web statique ultra-performant avec S3, CloudFront CDN, Route 53 et certificat SSL automatique.',
  longDescription: 'Architecture serverless pour sites statiques : S3 héberge les fichiers, CloudFront les distribue mondialement, ACM gère le SSL gratuitement, Route 53 assure le DNS, et WAFv2 protège contre les attaques DDoS.',
  category: 'web-app', provider: 'aws', difficulty: 'beginner',
  resourcesCount: 5, deploymentTime: '10 min',
  tags: ['S3', 'CloudFront', 'Route53', 'Static'],
  icon: '📄',
  features: [
    'S3 (hébergement statique)',
    'CloudFront (CDN mondial)',
    'ACM (certificat SSL gratuit)',
    'Route 53 (DNS)',
    'WAFv2 (protection DDoS)',
  ],
  diagramData: {
    nodes: [
      n('r53', 'aws_route53_zone',            'Route 53',   100, 200),
      n('cf',  'aws_cloudfront_distribution', 'CloudFront', 350, 200),
      n('s3',  'aws_s3_bucket',               'S3 Bucket',  600, 200),
      n('acm', 'aws_acm_certificate',         'ACM (SSL)',   350, 400),
      n('waf', 'aws_wafv2_web_acl',           'WAF',        100, 400),
    ],
    edges: [
      e('e1', 'r53', 'cf',  'DNS'),
      e('e2', 'cf',  's3',  'Origin'),
      e('e3', 'acm', 'cf',  'SSL'),
      e('e4', 'waf', 'cf',  'Protect'),
    ],
  },
};

const awsServerless: TemplateData = {
  id: 'aws-serverless-api',
  name: 'API Serverless AWS',
  description: 'API REST serverless avec API Gateway, Lambda, DynamoDB et Cognito pour l\'authentification.',
  longDescription: 'Stack serverless complète sur AWS : API Gateway v2 expose l\'API, Lambda exécute la logique métier, DynamoDB persiste les données, Cognito gère l\'authentification, et SQS découple les traitements asynchrones.',
  category: 'serverless', provider: 'aws', difficulty: 'intermediate',
  resourcesCount: 6, deploymentTime: '15 min',
  tags: ['Lambda', 'API Gateway', 'DynamoDB', 'Cognito'],
  icon: '⚡', popular: true,
  features: [
    'API Gateway v2 (REST / WebSocket)',
    'AWS Lambda (fonctions)',
    'Amazon DynamoDB',
    'Amazon Cognito (auth)',
    'CloudWatch (logs/métriques)',
    'SQS (queue asynchrone)',
  ],
  diagramData: {
    nodes: [
      n('cognito', 'aws_cognito_user_pool',    'Cognito',     100, 100),
      n('apigw',   'aws_apigatewayv2_api',     'API Gateway', 100, 300),
      n('lambda',  'aws_lambda_function',      'Lambda',      380, 200),
      n('ddb',     'aws_dynamodb_table',       'DynamoDB',    650, 100),
      n('sqs',     'aws_sqs_queue',            'SQS Queue',   650, 300),
      n('cw',      'aws_cloudwatch_log_group', 'CloudWatch',  900, 200),
    ],
    edges: [
      e('e1', 'cognito','apigw',  'Auth'),
      e('e2', 'apigw',  'lambda', 'Invoke'),
      e('e3', 'lambda', 'ddb',    'CRUD'),
      e('e4', 'lambda', 'sqs',    'Queue'),
      e('e5', 'lambda', 'cw',     'Logs'),
    ],
  },
};

const awsMLPipeline: TemplateData = {
  id: 'aws-ml-pipeline',
  name: 'Pipeline ML AWS',
  description: 'Pipeline Machine Learning complet avec SageMaker, S3, Glue et visualisation des résultats.',
  longDescription: 'Architecture MLOps sur AWS : données brutes dans S3, transformation via Glue, entraînement et déploiement de modèles avec SageMaker, images Docker dans ECR, et monitoring via CloudWatch.',
  category: 'ml', provider: 'aws', difficulty: 'advanced',
  resourcesCount: 7, deploymentTime: '30 min',
  tags: ['SageMaker', 'MLOps', 'Glue', 'ECR'],
  icon: '🤖',
  features: [
    'Amazon S3 (data lake)',
    'AWS Glue (ETL)',
    'Amazon SageMaker (training + serving)',
    'Amazon ECR (Docker images)',
    'SageMaker Endpoint',
    'CloudWatch (monitoring modèle)',
    'Secrets Manager',
  ],
  diagramData: {
    nodes: [
      n('s3raw', 'aws_s3_bucket',              'S3 Raw Data',   100, 200),
      n('glue',  'aws_glue_catalog_database',  'AWS Glue',      350, 200),
      n('s3proc','aws_s3_bucket',              'S3 Processed',  600, 100),
      n('sm',    'aws_sagemaker_model',        'SageMaker',     600, 300),
      n('ecr',   'aws_ecr_repository',         'ECR',           850, 100),
      n('ep',    'aws_sagemaker_endpoint',     'SM Endpoint',   850, 300),
      n('cw',    'aws_cloudwatch_log_group',   'CloudWatch',   1100, 200),
    ],
    edges: [
      e('e1', 's3raw', 'glue',  'Extract'),
      e('e2', 'glue',  's3proc','Load'),
      e('e3', 's3proc','sm',    'Train'),
      e('e4', 'ecr',   'sm',    'Image'),
      e('e5', 'sm',    'ep',    'Deploy'),
      e('e6', 'ep',    'cw',    'Monitor'),
    ],
  },
};

const awsEKS: TemplateData = {
  id: 'aws-eks-microservices',
  name: 'Microservices EKS',
  description: 'Architecture microservices sur Amazon EKS avec ALB Ingress, ECR et RDS Aurora.',
  longDescription: 'Déploiement de microservices sur Amazon EKS : ALB pour l\'ingress HTTP/HTTPS, ECR pour les images conteneurs, RDS Aurora pour la persistance SQL, ElastiCache Redis pour le cache, et Secrets Manager pour les credentials.',
  category: 'microservices', provider: 'aws', difficulty: 'advanced',
  resourcesCount: 6, deploymentTime: '35 min',
  tags: ['EKS', 'Kubernetes', 'Aurora', 'ElastiCache'],
  icon: '🛒',
  features: [
    'Amazon EKS (Kubernetes managé)',
    'AWS Load Balancer (ALB)',
    'Amazon ECR (registre images)',
    'Amazon RDS Aurora (PostgreSQL)',
    'ElastiCache Redis',
    'AWS Secrets Manager',
  ],
  diagramData: {
    nodes: [
      n('alb',   'aws_lb',                    'ALB Ingress',  100, 200),
      n('eks',   'aws_eks_cluster',           'EKS Cluster',  380, 200),
      n('ecr',   'aws_ecr_repository',        'ECR',          380, 400),
      n('aurora','aws_rds_cluster',           'RDS Aurora',   650, 100),
      n('redis', 'aws_elasticache_cluster',   'ElastiCache',  650, 300),
      n('sm',    'aws_secretsmanager_secret', 'Secrets Mgr',  900, 200),
    ],
    edges: [
      e('e1', 'alb',   'eks',   'Route'),
      e('e2', 'ecr',   'eks',   'Images'),
      e('e3', 'eks',   'aurora','SQL'),
      e('e4', 'eks',   'redis', 'Cache'),
      e('e5', 'eks',   'sm',    'Secrets'),
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// GCP TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

const gcpBigQuery: TemplateData = {
  id: 'gcp-bigquery-analytics',
  name: 'Analytics BigQuery',
  description: 'Plateforme analytics avec BigQuery, Dataflow pour l\'ingestion temps réel et monitoring.',
  longDescription: 'Architecture analytics serverless sur GCP : Pub/Sub pour l\'ingestion d\'événements, Dataflow pour le traitement temps réel, BigQuery comme data warehouse, et Cloud Storage pour le data lake.',
  category: 'analytics', provider: 'gcp', difficulty: 'intermediate',
  resourcesCount: 6, deploymentTime: '20 min',
  tags: ['BigQuery', 'Dataflow', 'Pub/Sub', 'Analytics'],
  icon: '📡',
  features: [
    'Google Pub/Sub (événements)',
    'Cloud Dataflow (ETL temps réel)',
    'BigQuery (data warehouse)',
    'Cloud Storage (data lake)',
    'BigQuery Tables',
    'Cloud Monitoring',
  ],
  diagramData: {
    nodes: [
      n('pubsub','google_pubsub_topic',         'Pub/Sub',      100, 200),
      n('df',    'google_dataflow_job',         'Dataflow',     350, 200),
      n('gcs',   'google_storage_bucket',       'Cloud Storage',600, 100),
      n('bq',    'google_bigquery_dataset',     'BigQuery',     600, 300),
      n('bqt',   'google_bigquery_table',       'BQ Tables',    850, 200),
      n('mon',   'google_monitoring_dashboard', 'Monitoring',   850, 400),
    ],
    edges: [
      e('e1', 'pubsub','df',  'Stream'),
      e('e2', 'df',    'gcs', 'Raw'),
      e('e3', 'df',    'bq',  'Load'),
      e('e4', 'gcs',   'bq',  'Batch'),
      e('e5', 'bq',    'bqt', 'Tables'),
      e('e6', 'bq',    'mon', 'Metrics'),
    ],
  },
};

const gcpGKE: TemplateData = {
  id: 'gcp-gke-microservices',
  name: 'Microservices GKE',
  description: 'Microservices sur Google Kubernetes Engine avec Cloud Armor et Cloud SQL.',
  longDescription: 'Architecture microservices sur GKE : Load Balancing pour l\'ingress, Cloud Armor pour la protection WAF, Artifact Registry pour les images, Cloud SQL pour la persistance, et Memorystore Redis pour le cache.',
  category: 'microservices', provider: 'gcp', difficulty: 'advanced',
  resourcesCount: 6, deploymentTime: '30 min',
  tags: ['GKE', 'Kubernetes', 'Cloud SQL', 'Artifact Registry'],
  icon: '🔵',
  features: [
    'GKE (Kubernetes managé)',
    'Cloud Load Balancing',
    'Cloud Armor (WAF)',
    'Artifact Registry (images)',
    'Cloud SQL (PostgreSQL)',
    'Memorystore Redis',
  ],
  diagramData: {
    nodes: [
      n('lb',    'google_compute_global_forwarding_rule','Cloud LB',          100, 200),
      n('armor', 'google_compute_security_policy',      'Cloud Armor',       100, 400),
      n('gke',   'google_container_cluster',            'GKE',               380, 200),
      n('ar',    'google_artifact_registry_repository', 'Artifact Registry', 380, 400),
      n('sql',   'google_sql_database_instance',        'Cloud SQL',         650, 100),
      n('mem',   'google_redis_instance',               'Memorystore',       650, 300),
    ],
    edges: [
      e('e1', 'lb',    'gke', 'Route'),
      e('e2', 'armor', 'lb',  'Protect'),
      e('e3', 'ar',    'gke', 'Images'),
      e('e4', 'gke',   'sql', 'Data'),
      e('e5', 'gke',   'mem', 'Cache'),
    ],
  },
};

const gcpServerless: TemplateData = {
  id: 'gcp-serverless',
  name: 'Serverless GCP',
  description: 'Architecture serverless avec Cloud Run, Cloud Functions, Firestore et Firebase Auth.',
  longDescription: 'Stack serverless GCP : Cloud Run pour les conteneurs serverless, Cloud Functions pour les triggers événementiels, Firestore pour les données NoSQL temps réel, Firebase Auth pour l\'authentification, et Pub/Sub pour la communication asynchrone.',
  category: 'serverless', provider: 'gcp', difficulty: 'intermediate',
  resourcesCount: 5, deploymentTime: '12 min',
  tags: ['Cloud Run', 'Functions', 'Firestore', 'Firebase'],
  icon: '☁️',
  features: [
    'Cloud Run (conteneurs serverless)',
    'Cloud Functions (triggers)',
    'Firestore (NoSQL temps réel)',
    'Firebase Authentication',
    'Google Pub/Sub',
  ],
  diagramData: {
    nodes: [
      n('fb',  'google_identity_platform_config','Firebase Auth',   100, 100),
      n('cr',  'google_cloud_run_service',       'Cloud Run',       350, 200),
      n('fn',  'google_cloudfunctions_function', 'Cloud Functions', 600, 100),
      n('fs',  'google_firestore_database',      'Firestore',       600, 300),
      n('ps',  'google_pubsub_topic',            'Pub/Sub',         850, 200),
    ],
    edges: [
      e('e1', 'fb', 'cr', 'Token'),
      e('e2', 'cr', 'fs', 'Read/Write'),
      e('e3', 'cr', 'ps', 'Publish'),
      e('e4', 'ps', 'fn', 'Trigger'),
      e('e5', 'fn', 'fs', 'Update'),
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// MICROSOFT FABRIC TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

const fabricLakehouse: TemplateData = {
  id: 'fabric-lakehouse',
  name: 'Fabric Lakehouse',
  description: 'Architecture Lakehouse Microsoft Fabric avec OneLake, notebooks Spark et rapports Power BI intégrés.',
  longDescription: 'Le Fabric Lakehouse centralise toutes les données dans OneLake (stockage Delta unifié), transforme via notebooks Spark ou Dataflows Gen2, et expose directement dans Power BI via Direct Lake sans déplacement de données.',
  category: 'fabric-analytics', provider: 'fabric', difficulty: 'intermediate',
  resourcesCount: 7, deploymentTime: '15 min',
  tags: ['Lakehouse', 'OneLake', 'Spark', 'Power BI'],
  icon: '🏠', popular: true,
  features: [
    'Microsoft Fabric Workspace',
    'Lakehouse (OneLake Delta)',
    'Notebooks Spark (Python/SQL)',
    'Dataflows Gen2 (ETL)',
    'Semantic Model (Power BI)',
    'Power BI Reports',
    'OneLake (stockage unifié)',
  ],
  diagramData: {
    nodes: [
      n('ws',  'fabric_workspace',      'Fabric Workspace', 100, 200),
      n('lh',  'fabric_lakehouse',      'Lakehouse',        380, 100),
      n('nb',  'fabric_notebook',       'Notebooks Spark',  380, 300),
      n('df',  'fabric_dataflow',       'Dataflows Gen2',   650, 100),
      n('ol',  'fabric_onelake',        'OneLake',          650, 300),
      n('sm',  'fabric_semantic_model', 'Semantic Model',   900, 200),
      n('pbi', 'fabric_report',         'Power BI',        1150, 200),
    ],
    edges: [
      e('e1', 'ws',  'lh',  'Contains'),
      e('e2', 'ws',  'nb',  'Contains'),
      e('e3', 'lh',  'df',  'Source'),
      e('e4', 'df',  'lh',  'Write'),
      e('e5', 'lh',  'ol',  'Delta'),
      e('e6', 'nb',  'lh',  'Write'),
      e('e7', 'lh',  'sm',  'Direct Lake'),
      e('e8', 'sm',  'pbi', 'Report'),
    ],
  },
};

const fabricDataWarehouse: TemplateData = {
  id: 'fabric-data-warehouse',
  name: 'Fabric Data Warehouse',
  description: 'Entrepôt de données moderne avec Fabric Warehouse, pipelines de données et rapports Power BI.',
  longDescription: 'Warehouse Fabric pour les analyses SQL classiques : ingestion via Data Pipeline depuis des sources externes, transformation SQL dans le Warehouse, modélisation sémantique et publication de rapports Power BI.',
  category: 'fabric-analytics', provider: 'fabric', difficulty: 'beginner',
  resourcesCount: 5, deploymentTime: '10 min',
  tags: ['Warehouse', 'SQL', 'Pipeline', 'Power BI'],
  icon: '🗄️',
  features: [
    'Fabric Data Warehouse (T-SQL)',
    'Data Pipelines (ingestion)',
    'Fabric Workspace',
    'Semantic Model Power BI',
    'Power BI Reports & Dashboards',
  ],
  diagramData: {
    nodes: [
      n('ws',  'fabric_workspace',      'Workspace',       100, 200),
      n('pip', 'fabric_pipeline',       'Data Pipeline',   380, 200),
      n('wh',  'fabric_warehouse',      'Data Warehouse',  650, 200),
      n('sm',  'fabric_semantic_model', 'Semantic Model',  900, 200),
      n('pbi', 'fabric_report',         'Power BI',       1150, 200),
    ],
    edges: [
      e('e1', 'ws',  'pip', 'Organize'),
      e('e2', 'pip', 'wh',  'Load'),
      e('e3', 'wh',  'sm',  'Model'),
      e('e4', 'sm',  'pbi', 'Visualize'),
    ],
  },
};

const fabricRealtime: TemplateData = {
  id: 'fabric-realtime-analytics',
  name: 'Analytics Temps Réel Fabric',
  description: 'Pipeline temps réel avec Fabric Eventstream, KQL Database et tableaux de bord Power BI.',
  longDescription: 'Architecture streaming avec Microsoft Fabric : Eventstream pour capturer les événements (IoT, clickstream, Kafka, Event Hubs), Eventhouse + KQL Database pour le stockage et la requête en temps réel, et Power BI pour la visualisation à la seconde.',
  category: 'iot', provider: 'fabric', difficulty: 'advanced',
  resourcesCount: 5, deploymentTime: '20 min',
  tags: ['Eventstream', 'KQL', 'Real-Time', 'IoT'],
  icon: '⚡',
  features: [
    'Fabric Eventstream (ingestion)',
    'Eventhouse (KQL Engine)',
    'KQL Database (stockage temps réel)',
    'Fabric Workspace',
    'Power BI (visualisation temps réel)',
  ],
  diagramData: {
    nodes: [
      n('ws',  'fabric_workspace',   'Fabric Workspace', 100, 200),
      n('es',  'fabric_eventstream', 'Eventstream',      380, 100),
      n('eh',  'fabric_eventhouse',  'Eventhouse',       380, 300),
      n('kdb', 'fabric_kql_database','KQL Database',     650, 200),
      n('pbi', 'fabric_report',      'Power BI RT',      900, 200),
    ],
    edges: [
      e('e1', 'ws',  'es',  'Contains'),
      e('e2', 'es',  'eh',  'Ingest'),
      e('e3', 'eh',  'kdb', 'Store'),
      e('e4', 'kdb', 'pbi', 'Query'),
    ],
  },
};

const fabricDataScience: TemplateData = {
  id: 'fabric-data-science',
  name: 'Data Science Fabric',
  description: 'Plateforme MLOps avec Fabric Lakehouse, notebooks ML, modèles et déploiement Power BI.',
  longDescription: 'Workflow Data Science complet dans Microsoft Fabric : préparation des données dans le Lakehouse, expérimentation ML avec notebooks Python et suivi MLflow intégré, enregistrement de modèles, et exposition des prédictions via Semantic Model Power BI.',
  category: 'ml', provider: 'fabric', difficulty: 'advanced',
  resourcesCount: 6, deploymentTime: '25 min',
  tags: ['MLflow', 'Notebooks', 'MLOps', 'Fabric'],
  icon: '🧪',
  features: [
    'Fabric Lakehouse (données features)',
    'Notebooks Python (expérimentation)',
    'Fabric ML Model (registre)',
    'Fabric Data Agent',
    'Semantic Model (prédictions)',
    'Power BI (visualisation)',
  ],
  diagramData: {
    nodes: [
      n('lh',  'fabric_lakehouse',      'Lakehouse',      100, 200),
      n('nb',  'fabric_notebook',       'Notebooks ML',   380, 100),
      n('mod', 'fabric_ml_model',       'Modèles ML',     380, 300),
      n('ag',  'fabric_data_agent',     'Data Agent',     650, 200),
      n('sm',  'fabric_semantic_model', 'Semantic Model', 900, 200),
      n('pbi', 'fabric_report',         'Power BI',      1150, 200),
    ],
    edges: [
      e('e1', 'lh',  'nb',  'Features'),
      e('e2', 'nb',  'mod', 'Train & Register'),
      e('e3', 'mod', 'ag',  'Score'),
      e('e4', 'ag',  'sm',  'Predict'),
      e('e5', 'sm',  'pbi', 'Report'),
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// MULTICLOUD TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

const multicloudDR: TemplateData = {
  id: 'multicloud-dr',
  name: 'Disaster Recovery Multi-Cloud',
  description: 'Plan de reprise d\'activité avec réplication cross-cloud Azure / AWS et basculement automatique.',
  longDescription: 'Architecture DR active-passive multi-cloud : workload primaire sur Azure (App Service + SQL), réplication continue vers AWS (Lambda + RDS Aurora), et basculement DNS automatique via Azure Traffic Manager en cas de panne.',
  category: 'network', provider: 'multicloud', difficulty: 'advanced',
  resourcesCount: 7, deploymentTime: '45 min',
  tags: ['DR', 'Multi-Cloud', 'Failover', 'Réplication'],
  icon: '🔄',
  features: [
    'Azure Traffic Manager (DNS failover)',
    'VPN Site-to-Site (Azure ↔ AWS)',
    'Azure App Service (primaire)',
    'AWS Lambda (secondaire)',
    'Azure SQL + AWS RDS (sync)',
    'Azure VPN Gateway',
    'AWS ALB',
  ],
  diagramData: {
    nodes: [
      n('tm',    'azurerm_traffic_manager_profile','Traffic Manager', 600, 50),
      n('azapp', 'azurerm_container_app',          'App Service',     300, 200),
      n('azsql', 'azurerm_mssql_database',         'Azure SQL',       100, 350),
      n('vpn',   'azurerm_vpn_gateway',            'VPN Gateway',     500, 350),
      n('awslb', 'aws_lb',                         'ALB',             900, 200),
      n('awsfn', 'aws_lambda_function',            'Lambda',         1100, 200),
      n('awsrds','aws_rds_cluster',                'RDS Aurora',     1100, 350),
    ],
    edges: [
      e('e1', 'tm',    'azapp', 'Primary'),
      e('e2', 'tm',    'awslb', 'Failover'),
      e('e3', 'azapp', 'azsql', 'Write'),
      e('e4', 'azsql', 'vpn',   'Replicate'),
      e('e5', 'vpn',   'awsrds','Sync'),
      e('e6', 'awslb', 'awsfn', 'Route'),
      e('e7', 'awsfn', 'awsrds','Read'),
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export const ALL_TEMPLATES: TemplateData[] = [
  // Azure
  azureWebApp3Tier,
  azureMicroservicesAKS,
  azureServerless,
  azureDataLake,
  azureLandingZone,
  azureHubSpoke,
  // AWS
  awsWebApp,
  awsServerless,
  awsMLPipeline,
  awsEKS,
  // GCP
  gcpBigQuery,
  gcpGKE,
  gcpServerless,
  // Microsoft Fabric
  fabricLakehouse,
  fabricDataWarehouse,
  fabricRealtime,
  fabricDataScience,
  // MultiCloud
  multicloudDR,
].map(colorizeTemplate);

export const PROVIDER_LABELS: Record<TemplateProvider, string> = {
  azure:      'Azure',
  aws:        'AWS',
  gcp:        'Google Cloud',
  fabric:     'Microsoft Fabric',
  multicloud: 'Multi-Cloud',
};

export const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  'web-app':          'Web App',
  'microservices':    'Microservices',
  'data':             'Data & Analytics',
  'serverless':       'Serverless',
  'network':          'Réseau & DR',
  'security':         'Sécurité',
  'analytics':        'Analytics',
  'ml':               'Machine Learning',
  'fabric-analytics': 'Fabric Analytics',
  'iot':              'IoT & Temps Réel',
};

export const DIFFICULTY_LABELS: Record<TemplateDifficulty, string> = {
  beginner:     'Débutant',
  intermediate: 'Intermédiaire',
  advanced:     'Avancé',
};
